import React, { useState, useEffect } from 'react';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Building2, Users } from 'lucide-react';

/**
 * Lista os usuários do condomínio selecionado no header.
 */
const MeuCondominio = () => {
  const { condominioSelecionado, getCondominioId } = useCondominio();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });

  const condominioId = getCondominioId?.() || condominioSelecionado?.condominio_id;

  useEffect(() => {
    if (!condominioId) {
      setUsuarios([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams();
    params.append('condominio_id', condominioId);
    params.append('page', pagination.page);
    params.append('limit', pagination.limit);
    api.get(`/usuarios?${params}`)
      .then((res) => {
        const data = res.data.data?.usuarios || res.data.data || [];
        const validos = data.filter(u => u && (u.usuario?.usuario_id || u.usuario_id || u.usuario_nome));
        setUsuarios(validos);
        const pag = res.data.data?.pagination || {};
        const total = pag.totalItems ?? pag.total ?? 0;
        const pages = pag.totalPages ?? pag.pages ?? Math.max(1, Math.ceil(total / (pagination.limit || 10)));
        setPagination(prev => ({ ...prev, total, pages }));
      })
      .catch(() => setUsuarios([]))
      .finally(() => setLoading(false));
  }, [condominioId, pagination.page, pagination.limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getTipoLabel = (tipo) => {
    const map = { administrador: 'Administrador', sindico: 'Síndico', subsindico: 'Subsíndico', porteiro: 'Porteiro', morador: 'Morador', funcionario: 'Funcionário', financeiro: 'Financeiro', juridico: 'Jurídico' };
    return map[tipo] || tipo || '—';
  };

  if (!condominioId) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <Building2 className="h-8 w-8 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Meu Condomínio</h1>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center text-amber-800">
          <Users className="h-12 w-12 mx-auto text-amber-500 mb-3" />
          <p className="font-medium">Selecione um condomínio no menu superior para ver os usuários.</p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meu Condomínio</h1>
          <p className="text-sm text-gray-500">{condominioSelecionado?.condominio_nome || 'Condomínio selecionado'} — Usuários</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidade</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500">Nenhum usuário neste condomínio.</td>
                </tr>
              ) : (
                usuarios.map((u) => {
                  const data = u.usuario || u;
                  return (
                    <tr key={u.usuario_condominio_id || u.usuario_id || data.usuario_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{data.usuario_nome || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{data.usuario_email || '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{getTipoLabel(u.tipo_acesso || data.usuario_tipo)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {u.apartamento ? `${u.apartamento.apartamento_bloco || ''} - ${u.apartamento.apartamento_numero || ''}`.trim() || '—' : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-gray-700">
            Página {pagination.page} de {pagination.pages} · Total: {pagination.total} usuário(s)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages || pagination.pages <= 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeuCondominio;
